import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import { toast } from 'react-hot-toast';
import { Loader2, Plus, Copy, Trash2, Save, X, Edit2, Check, Lock, ShieldCheck, ShieldAlert } from 'lucide-react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useAuth } from '../App.jsx';

const getCurrentDateTimeLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const GestionarRepertoriosModal = ({ isOpen, onClose, tramite, onUpdated }) => {
    const { user } = useAuth();
    
    // Facultad de edición: Admins, Boss o funcionarios con el flag explícito can_edit_repertorios
    const canEdit = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'boss' || Boolean(user?.can_edit_repertorios);

    const [repertorios, setRepertorios] = useState([]);
    const [contratos, setContratos] = useState([]);
    const [estados, setEstados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [subiendo, setSubiendo] = useState(false);

    // RF-05: Estado de exención de repertorio (No requiere repertorio)
    const [isExento, setIsExento] = useState(Boolean(tramite?.exento_repertorio));
    const [motivoExento, setMotivoExento] = useState(tramite?.motivo_exento_repertorio || '');
    const [togglingExento, setTogglingExento] = useState(false);

    // Estados para edición inline de repertorios asignados
    const [editingRepId, setEditingRepId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [guardandoEdicion, setGuardandoEdicion] = useState(false);
    
    // Formularios dinámicos para agregar nuevos
    const [forms, setForms] = useState([{
        numero_repertorio: '',
        fecha_repertorio: getCurrentDateTimeLocal(),
        nombre_requirente: '',
        numero_contacto: '',
        contrato_id: '',
        contrato_otro: '',
        estado_id: ''
    }]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && tramite) {
            setIsExento(Boolean(tramite.exento_repertorio));
            setMotivoExento(tramite.motivo_exento_repertorio || '');
            fetchData();
        } else {
            setForms([{
                numero_repertorio: '',
                fecha_repertorio: getCurrentDateTimeLocal(),
                nombre_requirente: tramite?.nombre_solicitante || '',
                numero_contacto: tramite?.telefono || '',
                contrato_id: '',
                contrato_otro: '',
                estado_id: ''
            }]);
            setEditingRepId(null);
        }
    }, [isOpen, tramite]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [repRes, conRes, estRes] = await Promise.all([
                api.get(`/tramites/${tramite.id}/repertorios`),
                api.get('/contratos'),
                api.get('/estados')
            ]);
            
            setRepertorios(Array.isArray(repRes.data) ? repRes.data : []);
            setContratos(Array.isArray(conRes.data) ? conRes.data : []);
            
            // Filtrar solo estados tipo repertorio
            const estData = Array.isArray(estRes.data) ? estRes.data : [];
            const repEstados = estData.filter(e => e.tipo === 'repertorio');
            setEstados(repEstados);
            
            // Establecer "Ingresado" por defecto
            const defaultEstado = repEstados.find(e => e.nombre === 'Ingresado');
            if (defaultEstado) {
                setForms([{
                    numero_repertorio: '',
                    fecha_repertorio: getCurrentDateTimeLocal(),
                    nombre_requirente: tramite?.nombre_solicitante || '',
                    numero_contacto: tramite?.telefono || '',
                    contrato_id: '',
                    contrato_otro: '',
                    estado_id: defaultEstado.id
                }]);
            }
        } catch (error) {
            toast.error("Error al cargar datos.");
        } finally {
            setLoading(false);
        }
    };

    const handleAddForm = () => {
        const defaultEstado = estados.find(e => e.nombre === 'Ingresado');
        const firstForm = forms[0];
        setForms([...forms, {
            numero_repertorio: '',
            fecha_repertorio: firstForm?.fecha_repertorio || getCurrentDateTimeLocal(),
            nombre_requirente: firstForm?.nombre_requirente || tramite?.nombre_solicitante || '',
            numero_contacto: firstForm?.numero_contacto || tramite?.telefono || '',
            contrato_id: '',
            contrato_otro: '',
            estado_id: defaultEstado ? defaultEstado.id : ''
        }]);
    };

    const handleRemoveForm = (index) => {
        const newForms = forms.filter((_, i) => i !== index);
        setForms(newForms);
    };

    const handleChangeForm = (index, field, value) => {
        const newForms = [...forms];
        newForms[index][field] = value;
        setForms(newForms);
    };

    const handleCopyRequirente = (index) => {
        if (index === 0) return;
        const newForms = [...forms];
        newForms[index].nombre_requirente = newForms[0].nombre_requirente;
        newForms[index].numero_contacto = newForms[0].numero_contacto;
        newForms[index].fecha_repertorio = newForms[0].fecha_repertorio;
        setForms(newForms);
        toast.success("Datos del requirente copiados de la fila #1");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setSubiendo(true);
            
            // Validar que cada formulario tenga número, fecha, requirente, contrato y estado
            for (let i = 0; i < forms.length; i++) {
                const f = forms[i];
                if (!f.numero_repertorio || !f.fecha_repertorio || !f.nombre_requirente || !f.contrato_id || !f.estado_id) {
                    toast.error(`La fila #${i + 1} tiene campos obligatorios vacíos (N° Repertorio o Naturaleza del Acto).`);
                    setSubiendo(false);
                    return;
                }
            }

            // Guardar individualmente cada repertorio con su número y tipo de acto
            for (const f of forms) {
                await api.post(`/tramites/${tramite.id}/repertorios`, f);
            }

            toast.success("Repertorios asignados exitosamente.");
            onUpdated();
            fetchData();
            
            const defaultEstado = estados.find(e => e.nombre === 'Ingresado');
            setForms([{
                numero_repertorio: '',
                fecha_repertorio: getCurrentDateTimeLocal(),
                nombre_requirente: tramite?.nombre_solicitante || '',
                numero_contacto: tramite?.telefono || '',
                contrato_id: '',
                contrato_otro: '',
                estado_id: defaultEstado ? defaultEstado.id : ''
            }]);
        } catch (error) {
            toast.error(error.response?.data?.message || error.response?.data?.error || "Error al guardar repertorio.");
        } finally {
            setSubiendo(false);
        }
    };

    // ── Funciones de Edición de Repertorio Asignado ──
    const handleStartEdit = (rep) => {
        if (!canEdit) {
            toast.error("No tienes facultades para editar repertorios ya asignados.");
            return;
        }
        setEditingRepId(rep.id);
        setEditForm({
            numero_repertorio: rep.numero_repertorio,
            contrato_id: rep.contrato_id,
            contrato_otro: rep.contrato_otro || '',
            nombre_requirente: rep.nombre_requirente || '',
            numero_contacto: rep.numero_contacto || '',
            estado_id: rep.estado_id
        });
    };

    const handleCancelEdit = () => {
        setEditingRepId(null);
        setEditForm({});
    };

    const handleSaveEdit = async (repId) => {
        if (!editForm.numero_repertorio || !editForm.contrato_id || !editForm.nombre_requirente) {
            toast.error("El número, naturaleza y requirente son obligatorios.");
            return;
        }

        setGuardandoEdicion(true);
        try {
            await api.put(`/tramites/${tramite.id}/repertorios/${repId}`, editForm);
            toast.success("Repertorio modificado exitosamente.");
            setEditingRepId(null);
            setEditForm({});
            fetchData();
            onUpdated();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error al actualizar el repertorio.");
        } finally {
            setGuardandoEdicion(false);
        }
    };

    const handleDeleteRepertorio = async (repId) => {
        if (!canEdit) {
            toast.error("No tienes facultades para eliminar repertorios asignados.");
            return;
        }

        const result = await Swal.fire({
            title: '¿Eliminar Repertorio?',
            text: '¿Seguro que deseas eliminar este repertorio del trámite?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;
        try {
            setLoading(true);
            await api.delete(`/tramites/${tramite.id}/repertorios/${repId}`);
            toast.success("Repertorio eliminado");
            onUpdated();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error al eliminar");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateEstado = async (repId, newEstadoId) => {
        try {
            await api.put(`/tramites/${tramite.id}/repertorios/${repId}`, { estado_id: newEstadoId });
            toast.success("Estado actualizado");
            fetchData();
            onUpdated();
        } catch (error) {
            toast.error("Error al actualizar el estado");
        }
    };

    // RF-05: Regla de Negocio para Trámites Exentos de Repertorio
    const handleToggleExento = async () => {
        if (isExento) {
            const result = await Swal.fire({
                title: '¿Revertir exención?',
                text: 'El trámite volverá a requerir números de repertorio para su cotización y gestión ordinaria.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, revertir',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#64748b'
            });

            if (!result.isConfirmed) return;

            try {
                setTogglingExento(true);
                const res = await api.post(`/tramites/${tramite.id}/toggle-exento-repertorio`, { exento: false });
                setIsExento(false);
                setMotivoExento('');
                toast.success(res.data?.message || 'Trámite requiere repertorio nuevamente.');
                if (onUpdated) onUpdated();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error al revertir exención.');
            } finally {
                setTogglingExento(false);
            }
        } else {
            const { value: motivo } = await Swal.fire({
                title: 'No Requiere Repertorio',
                text: 'Permite cotizar y procesar este trámite institucionalmente sin asignación de repertorio previo (ej. Alzamientos o Cancelaciones).',
                input: 'select',
                inputOptions: {
                    'Alzamiento': '🏷️ Alzamiento (Hipoteca / Gravamen)',
                    'Cancelación': '📜 Cancelación (Prohibición / Inscripción)',
                    'Excepción administrativa': '⚙️ Excepción administrativa / Gestión interna',
                    'Otro motivo': '📝 Otro motivo institucional'
                },
                inputPlaceholder: '— Seleccione el motivo institucional —',
                showCancelButton: true,
                confirmButtonText: 'Confirmar: No Requiere Repertorio',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#059669',
                cancelButtonColor: '#64748b',
                inputValidator: (val) => {
                    if (!val) return 'Debes seleccionar un motivo institucional';
                }
            });

            if (!motivo) return;

            try {
                setTogglingExento(true);
                const res = await api.post(`/tramites/${tramite.id}/toggle-exento-repertorio`, { exento: true, motivo });
                setIsExento(true);
                setMotivoExento(motivo);
                toast.success(res.data?.message || 'Trámite marcado como exento de repertorio.');
                if (onUpdated) onUpdated();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error al marcar como exento.');
            } finally {
                setTogglingExento(false);
            }
        }
    };

    const getContratoNombre = (cid) => {
        const c = contratos.find(x => x.id == cid);
        return c?.nombre === 'Otro' ? 'Otro' : c?.nombre;
    };

    if (!isOpen || !tramite) return null;

    return (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
            <div className="glass-card" style={{ width: '100%', maxWidth: '940px', margin: '2rem', padding: '2rem', maxHeight: '90vh', overflowY: 'auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h3 style={{ margin: 0, color: 'var(--copper)' }}>Gestionar Repertorios</h3>
                            {canEdit && (
                                <span style={{ fontSize: '0.72rem', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
                                    ✓ Facultad de Edición Activa
                                </span>
                            )}
                        </div>
                        <div className="text-muted" style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 500, color: 'var(--gray-200)', marginBottom: '0.25rem' }}>
                                Trámite #{tramite.numero_seguimiento || tramite.id}
                            </div>
                            <div>Solicitante: {tramite.nombre_solicitante}</div>
                        </div>
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '0.5rem' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* RF-05: Botón / Toggle Administrativo de Trámite Exento de Repertorio */}
                <div style={{
                    background: isExento ? '#ecfdf5' : '#f8fafc',
                    border: isExento ? '2px solid #10b981' : '1px solid #cbd5e1',
                    borderRadius: '10px',
                    padding: '1.1rem 1.35rem',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1.25rem',
                    boxShadow: isExento ? '0 4px 12px rgba(16, 185, 129, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.25s ease'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '10px',
                            background: isExento ? '#10b981' : '#e2e8f0',
                            border: isExento ? '1px solid #059669' : '1px solid #cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isExento ? '#ffffff' : '#059669',
                            flexShrink: 0,
                            boxShadow: isExento ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none'
                        }}>
                            <ShieldCheck size={24} />
                        </div>
                        <div>
                            <div style={{ 
                                fontWeight: 800, 
                                fontSize: '1rem', 
                                color: isExento ? '#065f46' : '#0f172a', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px' 
                            }}>
                                <span>{isExento ? 'Trámite Exento de Repertorio Previo' : '¿Trámite Exento de Repertorio?'}</span>
                                {isExento && (
                                    <span style={{ 
                                        fontSize: '0.72rem', 
                                        background: '#10b981', 
                                        color: '#ffffff', 
                                        padding: '2px 9px', 
                                        borderRadius: '12px', 
                                        fontWeight: 800,
                                        letterSpacing: '0.5px'
                                    }}>
                                        ACTIVO
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: '0.82rem', color: isExento ? '#047857' : '#475569', marginTop: '3px', lineHeight: 1.4 }}>
                                {isExento 
                                    ? `Este trámite está formalmente exento de repertorio (ej. Alzamiento o Cancelación). Permite cotizar y despachar directamente.${motivoExento ? ` Motivo: "${motivoExento}"` : ''}`
                                    : 'Aplica para trámites institucionales que no requieren asignación de repertorio previo (ej. Alzamientos o Cancelaciones).'}
                            </div>
                        </div>
                    </div>

                    <div style={{ flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={handleToggleExento}
                            disabled={togglingExento}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '0.6rem 1.25rem',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                background: isExento ? '#ffffff' : '#059669',
                                border: isExento ? '1.5px solid #ef4444' : 'none',
                                color: isExento ? '#dc2626' : '#ffffff',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                boxShadow: isExento ? '0 1px 3px rgba(239, 68, 68, 0.15)' : '0 2px 6px rgba(5, 150, 105, 0.25)',
                                transition: 'all 0.2s ease'
                            }}
                            title={isExento ? "Revertir a trámite ordinario con repertorio obligatorio" : "Marcar que este trámite no requiere repertorio"}
                        >
                            {togglingExento ? (
                                <><Loader2 size={16} className="spinner" /> Procesando...</>
                            ) : isExento ? (
                                <><ShieldAlert size={16} /> Revertir a Requiere Repertorio</>
                            ) : (
                                <><ShieldCheck size={16} /> No requiere repertorio</>
                            )}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-400)' }}>
                        <span className="spinner spinner-lg" style={{ margin: '0 auto 1rem', display: 'block' }} />
                        Cargando información...
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {/* Listado Actual de Repertorios con su Naturaleza/Acto */}
                        {repertorios.length > 0 && (
                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h4 style={{ margin: 0, color: 'var(--gray-200)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>Repertorios Asignados</span>
                                        <span style={{ fontSize: '0.75rem', background: '#3b82f620', color: '#60a5fa', padding: '2px 8px', borderRadius: '12px' }}>
                                            {repertorios.length} actos registrados
                                        </span>
                                    </h4>
                                    {!canEdit && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Lock size={12} /> Modificación restringida a administradores
                                        </span>
                                    )}
                                </div>

                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {repertorios.map(rep => {
                                        const isEditingThis = editingRepId === rep.id;

                                        return (
                                            <li key={rep.id} style={{ 
                                                display: 'flex', flexDirection: 'column', gap: '0.75rem',
                                                padding: '1rem', background: rep.is_vencido ? 'rgba(239, 68, 68, 0.1)' : 'rgba(0,0,0,0.2)', 
                                                borderRadius: '8px', border: isEditingThis ? '2px solid #3b82f6' : (rep.is_vencido ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255,255,255,0.05)')
                                            }}>
                                                {/* MODO EDICIÓN INLINE */}
                                                {isEditingThis ? (
                                                    <div style={{ display: 'grid', gridTemplateColumns: '120px minmax(220px, 1.5fr) 1fr auto', gap: '10px', alignItems: 'center' }}>
                                                        <div>
                                                            <label style={{ fontSize: '0.72rem', color: 'var(--gray-300)', display: 'block', marginBottom: '2px' }}>N° Repertorio</label>
                                                            <input 
                                                                type="number"
                                                                className="form-control"
                                                                style={{ height: '34px', fontSize: '0.85rem' }}
                                                                value={editForm.numero_repertorio}
                                                                onChange={e => setEditForm({ ...editForm, numero_repertorio: e.target.value })}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '0.72rem', color: 'var(--gray-300)', display: 'block', marginBottom: '2px' }}>Naturaleza / Acto</label>
                                                            <Select 
                                                                options={contratos.map(c => ({ value: c.id, label: c.nombre }))}
                                                                value={editForm.contrato_id ? { value: editForm.contrato_id, label: contratos.find(c => c.id == editForm.contrato_id)?.nombre } : null}
                                                                onChange={selected => setEditForm({ ...editForm, contrato_id: selected ? selected.value : '' })}
                                                                placeholder="Buscar acto (Compraventa, Hipoteca...)"
                                                                isClearable
                                                                isSearchable
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                closeMenuOnScroll={true}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: '34px',
                                                                        height: '34px',
                                                                        borderRadius: '0.375rem',
                                                                        borderColor: 'var(--border-color, #cbd5e1)',
                                                                        backgroundColor: '#ffffff',
                                                                        boxShadow: 'none',
                                                                        fontSize: '0.85rem',
                                                                        '&:hover': { borderColor: 'var(--primary)' }
                                                                    }),
                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        height: '34px',
                                                                        padding: '0 8px',
                                                                    }),
                                                                    input: (base) => ({
                                                                        ...base,
                                                                        margin: '0px',
                                                                        color: '#0f172a'
                                                                    }),
                                                                    singleValue: (base) => ({
                                                                        ...base,
                                                                        color: '#0f172a',
                                                                        fontWeight: 500
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        zIndex: 99999,
                                                                        minWidth: '280px',
                                                                        backgroundColor: '#ffffff',
                                                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                                                                    }),
                                                                    menuPortal: base => ({ ...base, zIndex: 99999 }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        color: '#0f172a',
                                                                        backgroundColor: state.isFocused ? '#e2e8f0' : (state.isSelected ? '#cbd5e1' : '#ffffff'),
                                                                        fontSize: '0.85rem',
                                                                        cursor: 'pointer'
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '0.72rem', color: 'var(--gray-300)', display: 'block', marginBottom: '2px' }}>Requirente</label>
                                                            <input 
                                                                type="text"
                                                                className="form-control"
                                                                style={{ height: '34px', fontSize: '0.85rem' }}
                                                                value={editForm.nombre_requirente}
                                                                onChange={e => setEditForm({ ...editForm, nombre_requirente: e.target.value })}
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '6px', marginTop: '16px' }}>
                                                            <button 
                                                                className="btn btn-primary btn-sm" 
                                                                onClick={() => handleSaveEdit(rep.id)}
                                                                disabled={guardandoEdicion}
                                                                style={{ padding: '0.4rem 0.75rem' }}
                                                                title="Guardar Cambios"
                                                            >
                                                                <Check size={14} /> Guardar
                                                            </button>
                                                            <button 
                                                                className="btn btn-secondary btn-sm" 
                                                                onClick={handleCancelEdit}
                                                                disabled={guardandoEdicion}
                                                                style={{ padding: '0.4rem 0.75rem' }}
                                                                title="Cancelar"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                        {getContratoNombre(editForm.contrato_id) === 'Otro' && (
                                                            <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
                                                                <label style={{ fontSize: '0.72rem', color: 'var(--gray-300)', display: 'block', marginBottom: '2px' }}>Especifique Otro Contrato *</label>
                                                                <input 
                                                                    type="text"
                                                                    className="form-control"
                                                                    style={{ height: '34px', fontSize: '0.85rem' }}
                                                                    value={editForm.contrato_otro || ''}
                                                                    onChange={e => setEditForm({ ...editForm, contrato_otro: e.target.value })}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    /* VISTA NORMAL DEL REPERTORIO */
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ fontSize: '0.9rem' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ fontWeight: 'bold', color: 'var(--copper)', fontSize: '1.05rem' }}>
                                                                    Repertorio #{rep.numero_repertorio}
                                                                </span>
                                                                <span style={{ 
                                                                    background: '#e0f2fe', 
                                                                    color: '#0369a1', 
                                                                    fontSize: '0.75rem', 
                                                                    fontWeight: 700, 
                                                                    padding: '2px 8px', 
                                                                    borderRadius: '4px' 
                                                                }}>
                                                                    {rep.contrato?.nombre === 'Otro' ? rep.contrato_otro : (rep.contrato?.nombre || 'Sin Naturaleza')}
                                                                </span>
                                                                {rep.is_vencido && (
                                                                    <span style={{ padding: '2px 6px', background: 'var(--error)', color: 'white', borderRadius: 4, fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                                        Vencido
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '0.35rem' }}>
                                                                Requirente: {rep.nombre_requirente} | Fecha Ingreso: {new Date(rep.fecha_repertorio).toLocaleString('es-CL', { hour12: false, dateStyle: 'short', timeStyle: 'short' })}
                                                                {rep.fecha_vencimiento && ` | Vence: ${new Date(rep.fecha_vencimiento).toLocaleString('es-CL', { hour12: false, dateStyle: 'short', timeStyle: 'short' })}`}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <select 
                                                                className="form-control" 
                                                                style={{ height: '32px', padding: '0 0.5rem', fontSize: '0.8rem', width: 'auto' }}
                                                                value={rep.estado_id}
                                                                onChange={(e) => handleUpdateEstado(rep.id, e.target.value)}
                                                            >
                                                                {estados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                                                            </select>

                                                            {/* BOTÓN EDITAR (SOLO CON FACULTAD) */}
                                                            {canEdit ? (
                                                                <button 
                                                                    className="btn btn-secondary btn-sm" 
                                                                    onClick={() => handleStartEdit(rep)} 
                                                                    style={{ padding: '0.45rem' }} 
                                                                    title="Editar N° de Repertorio o Naturaleza del Acto"
                                                                >
                                                                    <Edit2 size={15} />
                                                                </button>
                                                            ) : null}

                                                            {/* BOTÓN ELIMINAR (SOLO CON FACULTAD) */}
                                                            {canEdit ? (
                                                                <button 
                                                                    className="btn btn-error btn-sm" 
                                                                    onClick={() => handleDeleteRepertorio(rep.id)} 
                                                                    style={{ padding: '0.45rem' }} 
                                                                    title="Eliminar Repertorio"
                                                                >
                                                                    <Trash2 size={15} />
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                        {/* Formulario Agregar Nuevos Repertorios (Individual: Número por Número) */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, color: 'var(--gray-200)' }}>Asignar Repertorios (Número por Número)</h4>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                                        Cada repertorio se asocia a su propia naturaleza del acto (ej. Compraventa, Hipoteca, Prohibición).
                                    </p>
                                </div>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddForm} style={{ display: 'flex', gap: '6px' }}>
                                    <Plus size={16}/> Añadir Fila
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {forms.map((f, index) => (
                                    <div key={index} style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px', position: 'relative' }}>
                                        <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px' }}>
                                            {index > 0 && (
                                                <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => handleCopyRequirente(index)} title="Copiar Datos del Requirente">
                                                    <Copy size={12} style={{ marginRight: '4px' }}/> Copiar Requirente
                                                </button>
                                            )}
                                            {forms.length > 1 && (
                                                <button type="button" className="btn btn-error btn-sm" style={{ padding: '0.25rem 0.5rem' }} onClick={() => handleRemoveForm(index)} title="Eliminar Fila">
                                                    <Trash2 size={14}/>
                                                </button>
                                            )}
                                        </div>

                                        <h5 style={{ margin: '0 0 1rem 0', color: 'var(--copper)', fontSize: '0.95rem' }}>
                                            Repertorio #{index + 1}
                                        </h5>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                            
                                            {/* N° DE REPERTORIO INDIVIDUAL */}
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>N° de Repertorio *</label>
                                                <input 
                                                    type="number" 
                                                    min="1" 
                                                    required 
                                                    value={f.numero_repertorio} 
                                                    onChange={e => handleChangeForm(index, 'numero_repertorio', e.target.value)} 
                                                    placeholder="Ej: 10121" 
                                                />
                                            </div>

                                            {/* CONTRATO / NATURALEZA DEL ACTO */}
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>Contrato / Naturaleza del Acto *</label>
                                                <Select 
                                                    options={contratos.map(c => ({ value: c.id, label: c.nombre }))}
                                                    value={f.contrato_id ? { value: f.contrato_id, label: contratos.find(c => c.id == f.contrato_id)?.nombre } : null}
                                                    onChange={selected => handleChangeForm(index, 'contrato_id', selected ? selected.value : '')}
                                                    placeholder="Seleccione acto (Compraventa, Hipoteca...)"
                                                    isClearable
                                                    isSearchable
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    closeMenuOnScroll={true}
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '40px',
                                                            borderRadius: '0.375rem',
                                                            borderColor: 'var(--border-color, #cbd5e1)',
                                                            backgroundColor: '#ffffff',
                                                            boxShadow: 'none',
                                                            '&:hover': { borderColor: 'var(--primary)' }
                                                        }),
                                                        singleValue: (base) => ({
                                                            ...base,
                                                            color: '#0f172a',
                                                            fontWeight: 500
                                                        }),
                                                        input: (base) => ({
                                                            ...base,
                                                            color: '#0f172a'
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            zIndex: 99999,
                                                            backgroundColor: '#ffffff',
                                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                                                        }),
                                                        menuPortal: base => ({ ...base, zIndex: 99999 }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            color: '#0f172a',
                                                            backgroundColor: state.isFocused ? '#e2e8f0' : (state.isSelected ? '#cbd5e1' : '#ffffff'),
                                                            cursor: 'pointer'
                                                        })
                                                    }}
                                                />
                                            </div>

                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>Fecha y Hora *</label>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <input 
                                                        type="date" 
                                                        className="form-control" 
                                                        required 
                                                        value={f.fecha_repertorio ? f.fecha_repertorio.split('T')[0] : ''} 
                                                        onChange={e => {
                                                            const t = f.fecha_repertorio && f.fecha_repertorio.includes('T') ? f.fecha_repertorio.split('T')[1] : '12:00';
                                                            handleChangeForm(index, 'fecha_repertorio', `${e.target.value}T${t}`);
                                                        }} 
                                                    />
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        required 
                                                        placeholder="HH:mm" 
                                                        maxLength={5} 
                                                        style={{ width: '80px', textAlign: 'center' }}
                                                        value={f.fecha_repertorio && f.fecha_repertorio.includes('T') ? f.fecha_repertorio.split('T')[1].substring(0,5) : ''} 
                                                        onChange={e => {
                                                            let val = e.target.value.replace(/[^0-9]/g, '');
                                                            if (val.length > 2) val = val.substring(0,2) + ':' + val.substring(2,4);
                                                            const d = f.fecha_repertorio && f.fecha_repertorio.includes('T') ? f.fecha_repertorio.split('T')[0] : new Date().toISOString().split('T')[0];
                                                            handleChangeForm(index, 'fecha_repertorio', `${d}T${val}`);
                                                        }} 
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>Nombre Requirente *</label>
                                                <input type="text" required value={f.nombre_requirente} onChange={e => handleChangeForm(index, 'nombre_requirente', e.target.value)} />
                                            </div>

                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>N° Contacto</label>
                                                <input type="text" value={f.numero_contacto} onChange={e => handleChangeForm(index, 'numero_contacto', e.target.value)} />
                                            </div>

                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label>Estado Inicial *</label>
                                                <select required value={f.estado_id} onChange={e => handleChangeForm(index, 'estado_id', e.target.value)}>
                                                    {estados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                                                </select>
                                            </div>

                                            {getContratoNombre(f.contrato_id) === 'Otro' && (
                                                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                                                    <label>Especifique Otro Contrato *</label>
                                                    <input type="text" required value={f.contrato_otro} onChange={e => handleChangeForm(index, 'contrato_otro', e.target.value)} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                                    <button type="submit" className="btn btn-primary" disabled={subiendo}>
                                        {subiendo ? <><span className="spinner"/> Guardando...</> : <><Save size={16} /> Guardar Repertorios</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GestionarRepertoriosModal;
